import { useState, useEffect } from 'react'
import './App.css'

function App() {
  // All state declarations first
  const [selectedPriceList, setSelectedPriceList] = useState('');
  const [tableData, setTableData] = useState([]);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [passcode, setPasscode] = useState('');
  const [authError, setAuthError] = useState('');
  const [companies, setCompanies] = useState([
    { id: 1, pricingFile: null, locationFile: null },
    { id: 2, pricingFile: null, locationFile: null },
    { id: 3, pricingFile: null, locationFile: null }
  ]);
  const [combinedError, setCombinedError] = useState(null);
  const [driveLink, setDriveLink] = useState('');
  const [driveLinkError, setDriveLinkError] = useState(null);
  const [isProcessingDrive, setIsProcessingDrive] = useState(false);
  const [isGoogleApiInitialized, setIsGoogleApiInitialized] = useState(false);
  const [accessToken, setAccessToken] = useState(null);
  const [driveFiles, setDriveFiles] = useState({
    pricingFile: { link: '', content: null },
    locationFile: { link: '', content: null }
  });
  const [driveError, setDriveError] = useState(null);
  const [driveCompanies, setDriveCompanies] = useState([
    { 
      id: 1,
      pricingFile: { link: '', content: null },
      locationFile: { link: '', content: null }
    }
  ]);

  // Helper functions defined before useEffect
  const getStoredToken = () => {
    const tokenData = localStorage.getItem('driveToken');
    if (!tokenData) return null;
    
    const { token, expiry } = JSON.parse(tokenData);
    
    // Check if token is expired (with 5-minute buffer)
    if (Date.now() > expiry - 300000) {
      localStorage.removeItem('driveToken');
      return null;
    }
    
    return token;
  };

  const saveToken = (tokenResponse) => {
    const expiresIn = tokenResponse.expires_in || 3600; // Default to 1 hour
    const expirationTime = Date.now() + (expiresIn * 1000);
    
    const tokenData = {
      token: tokenResponse.access_token,
      expiry: expirationTime
    };
    
    localStorage.setItem('driveToken', JSON.stringify(tokenData));
    setAccessToken(tokenResponse.access_token);
  };

  // Now useEffects can safely use the helper functions
  useEffect(() => {
    const token = getStoredToken();
    if (token) {
      setAccessToken(token);
    }
  }, []);

  useEffect(() => {
    if (localStorage.getItem('isAuthenticated') === 'true') {
      setIsAuthenticated(true);
    }
  }, []);

  useEffect(() => {
    if (selectedPriceList) {
      const fileName = {
        'urgent': 'urgent-care.csv',
        'concierge': 'concierge.csv',
        'imaging': 'imaging.csv'
      }[selectedPriceList];

      fetch(`/${fileName}`)
        .then(response => response.text())
        .then(csv => {
          const rows = csv.split('\n')
            .filter(row => row.trim() !== '');  // Remove empty rows
          
          // Create data array and remove duplicates based on both code and description
          const data = rows.slice(1)
            .map(row => {
              const [code, description] = parseCSVLine(row);
              return { code, description };
            })
            .filter((row, index, self) => 
              index === self.findIndex((t) => 
                t.code === row.code && t.description === row.description
              )
            );

          setTableData(data);
        })
        .catch(error => console.error('Error loading CSV:', error));
    }
  }, [selectedPriceList]);

  useEffect(() => {
    const loadGapiAndInitialize = () => {
      // First, load the gapi.client
      window.gapi.load('client', async () => {
        try {
          console.log('Starting GAPI initialization...');
          console.log('Client ID available:', !!import.meta.env.VITE_GOOGLE_CLIENT_ID);
          console.log('API Key available:', !!import.meta.env.VITE_GOOGLE_API_KEY);

          // Initialize the GAPI client first
          await window.gapi.client.init({
            apiKey: import.meta.env.VITE_GOOGLE_API_KEY,
            clientId: import.meta.env.VITE_GOOGLE_CLIENT_ID, // Make sure this is included
            discoveryDocs: ['https://www.googleapis.com/discovery/v1/apis/drive/v3/rest'],
            scope: 'https://www.googleapis.com/auth/drive.readonly'
          });

          console.log('GAPI client initialized successfully');

          // Then initialize the token client
          if (window.google && window.google.accounts) {
            const tokenClient = google.accounts.oauth2.initTokenClient({
              client_id: import.meta.env.VITE_GOOGLE_CLIENT_ID,
              scope: 'https://www.googleapis.com/auth/drive.readonly',
              callback: (response) => {
                if (response.error) {
                  console.error('Token client error:', response.error);
                  setDriveLinkError(response.error);
                  return;
                }
                console.log('Token client initialized successfully');
                setIsGoogleApiInitialized(true);
              }
            });
            console.log('Token client created:', !!tokenClient);
          } else {
            console.error('Google Identity Services not loaded');
          }
        } catch (error) {
          console.error('Error initializing Google API:', error);
          setDriveLinkError(`Failed to initialize Google API: ${error.message}`);
        }
      });
    };

    // Check if GAPI is available
    if (window.gapi) {
      console.log('GAPI found, initializing...');
      loadGapiAndInitialize();
    } else {
      console.log('GAPI not found, waiting...');
      const checkGapi = setInterval(() => {
        if (window.gapi) {
          console.log('GAPI found after waiting');
          clearInterval(checkGapi);
          loadGapiAndInitialize();
        }
      }, 100);

      // Clear interval after 10 seconds if GAPI never loads
      setTimeout(() => {
        clearInterval(checkGapi);
        if (!window.gapi) {
          console.error('GAPI failed to load after 10 seconds');
          setDriveLinkError('Failed to load Google API. Please refresh the page.');
        }
      }, 10000);
    }

    // Cleanup
    return () => {
      // Add any cleanup needed
    };
  }, []);

  // Add this helper function near the top of your component
  const parseCSVLine = (line) => {
    const entries = [];
    let entry = '';
    let withinQuotes = false;
    
    for (let i = 0; i < line.length; i++) {
      const char = line[i];
      
      if (char === '"') {
        withinQuotes = !withinQuotes;
      } else if (char === ',' && !withinQuotes) {
        entries.push(entry.trim());
        entry = '';
      } else {
        entry += char;
      }
    }
    
    // Push the last entry
    entries.push(entry.trim());
    return entries;
  };

  // If not authenticated, show passcode screen
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="bg-white p-8 rounded-lg shadow-md w-96">
          <h1 className="text-2xl font-bold mb-6 text-center">Jupiter Plans</h1>
          <form onSubmit={(e) => {
            e.preventDefault();
            if (passcode === 'ins24jupiter') {
              setIsAuthenticated(true);
              localStorage.setItem('isAuthenticated', 'true');
              setAuthError('');
            } else {
              setAuthError('Invalid passcode');
            }
          }}>
            <div className="mb-4">
              <label className="block text-gray-700 mb-2">Enter Passcode</label>
              <input
                type="password"
                value={passcode}
                onChange={(e) => setPasscode(e.target.value)}
                className="w-full p-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-400"
                placeholder="Enter passcode"
              />
            </div>
            {authError && (
              <p className="text-red-500 text-sm mb-4">{authError}</p>
            )}
            <button
              type="submit"
              className="w-full bg-blue-500 text-white p-2 rounded hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-400"
            >
              Submit
            </button>
          </form>
        </div>
      </div>
    );
  }

  // Handler for adding new company row
  const addCompanyRow = () => {
    const newId = Math.max(...companies.map(c => c.id)) + 1;
    setCompanies([...companies, { id: newId, pricingFile: null, locationFile: null }]);
  };

  // Handlers for file uploads
  const handleFileUpload = (companyId, fileType, event) => {
    const file = event.target.files[0];
    if (!file) return;

    const requiredColumns = fileType === 'pricing' 
      ? ['code', 'description']
      : ['jnpi', 'name', 'address', 'street', 'city', 'state', 'zip_code', 'latitude', 'longitude', 'phone', 'website'];

    validateCSVStructure(file, requiredColumns, (text) => {
      setCompanies(companies.map(company => {
        if (company.id === companyId) {
          return {
            ...company,
            [fileType === 'pricing' ? 'pricingFile' : 'locationFile']: { name: file.name, content: text }
          };
        }
        return company;
      }));
      setCombinedError(null);
    }, setCombinedError);
  };

  // Combined processing function
  const processCombinedFiles = () => {
    try {
      // Filter out companies without both files
      const validCompanies = companies.filter(c => c.pricingFile && c.locationFile);
      
      if (validCompanies.length === 0) {
        setCombinedError('Please upload at least one complete set of files');
        return;
      }

      // Process each company's files and combine results
      let combinedPricingContent = '';
      let combinedLocationContent = '';

      validCompanies.forEach((company, index) => {
        const processedPricing = generateCombinedPricingContent(
          company.pricingFile.content,
          company.locationFile.content
        );

        // Get location rows and filter empty ones
        const locationRows = company.locationFile.content.split('\n')
          .filter(row => row.trim() !== '' && row.replace(/,/g, '').trim() !== '');

        if (index === 0) {
          combinedPricingContent = processedPricing;
          combinedLocationContent = locationRows.join('\n');
        } else {
          // Remove header before joining for subsequent files
          combinedPricingContent += '\n' + processedPricing.split('\n').slice(1).join('\n');
          combinedLocationContent += '\n' + locationRows.slice(1).join('\n');
        }
      });

      // Download combined files
      downloadFile(combinedPricingContent, 'combined_pricing.csv');
      downloadFile(combinedLocationContent, 'combined_locations.csv');

    } catch (err) {
      setCombinedError('Error processing files: ' + err.message);
    }
  };

  // Helper function for file download
  const downloadFile = (content, filename) => {
    const blob = new Blob([content], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.setAttribute('hidden', '');
    a.setAttribute('href', url);
    a.setAttribute('download', filename);
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const generateCombinedPricingContent = (pricingFileContent, locationFileContent) => {
    try {
      // Split into rows and filter out empty rows more strictly
      const priceRows = pricingFileContent.split('\n')
        .filter(row => row.trim() !== '' && row.replace(/,/g, '').trim() !== '');
      const locationRows = locationFileContent.split('\n')
        .filter(row => row.trim() !== '' && row.replace(/,/g, '').trim() !== '');
      
      // Get headers using the parser
      const priceHeaders = parseCSVLine(priceRows[0].toLowerCase());
      const locationHeaders = parseCSVLine(locationRows[0].toLowerCase());
      
      // Find column indices
      const codeIndex = priceHeaders.indexOf('code');
      const priceIndex = priceHeaders.includes('price') ? 
        priceHeaders.indexOf('price') : priceHeaders.indexOf('cash_price');
      const detailIndex = priceHeaders.indexOf('detail');
      const jnpiIndex = locationHeaders.includes('jnpi') ? 
        locationHeaders.indexOf('jnpi') : locationHeaders.indexOf('npi');

      // Create output rows with required format
      const outputRows = [['jnpi', 'billing_code_type', 'billing_code', 'cash_price', 'detail']];

      // Skip headers and process only non-empty rows
      for (let i = 1; i < locationRows.length; i++) {
        const locationCols = parseCSVLine(locationRows[i]);
        const jnpi = locationCols[jnpiIndex];
        
        if (!jnpi || !locationCols.some(col => col.trim())) continue; // Skip if no JNPI/NPI or if row is effectively empty

        for (let j = 1; j < priceRows.length; j++) {
          const priceCols = parseCSVLine(priceRows[j]);
          // Skip if any required fields are empty
          if (!priceCols[codeIndex]?.trim() || !priceCols[priceIndex]?.trim()) continue;
          
          const code = priceCols[codeIndex].trim();
          const billingCodeType = code.includes('JP') ? 'JHCustom' : 'CPT';
          
          outputRows.push([
            jnpi,
            billingCodeType,
            code,
            priceCols[priceIndex].trim(),
            (priceCols[detailIndex] || '').trim()
          ]);
        }
      }

      return outputRows.map(row => row.join(',')).join('\n');
    } catch (err) {
      throw new Error('Error generating combined pricing content: ' + err.message);
    }
  };

  // Add these validation helpers
  const validateCSVContent = (content, requiredColumns, fileType) => {
    try {
      // Split into rows and get headers
      const rows = content.split('\n')
        .filter(row => row.trim() !== '');
      
      if (rows.length < 2) {
        throw new Error(`${fileType} file must contain at least a header row and one data row`);
      }

      const headers = parseCSVLine(rows[0].toLowerCase());
      
      // Check for required columns
      const missingColumns = requiredColumns.filter(col => 
        !headers.includes(col.toLowerCase())
      );

      if (missingColumns.length > 0) {
        throw new Error(`${fileType} file is missing required columns: ${missingColumns.join(', ')}`);
      }

      return true;
    } catch (error) {
      throw new Error(`Invalid ${fileType} file: ${error.message}`);
    }
  };

  // Update handleDriveLink to include validation
  const handleDriveLink = async (companyId, fileType, link) => {
    try {
      setDriveError(null);
      setIsProcessingDrive(true);

      // Get or refresh token
      let token = getStoredToken();
      if (!token) {
        token = await new Promise((resolve, reject) => {
          const tokenClient = google.accounts.oauth2.initTokenClient({
            client_id: import.meta.env.VITE_GOOGLE_CLIENT_ID,
            scope: 'https://www.googleapis.com/auth/drive.readonly',
            callback: (tokenResponse) => {
              if (tokenResponse.error) {
                reject(tokenResponse.error);
                return;
              }
              saveToken(tokenResponse);
              resolve(tokenResponse.access_token);
            }
          });
          tokenClient.requestAccessToken({ prompt: token ? 'none' : 'consent' });
        });
      }

      const fileId = extractFileIdFromLink(link);
      if (!fileId) {
        throw new Error('Invalid Drive link');
      }

      const response = await fetch(
        `https://docs.google.com/spreadsheets/d/${fileId}/export?format=csv`,
        {
          headers: {
            'Authorization': `Bearer ${token}`          }
        }
      );

      if (!response.ok) {
        throw new Error('Failed to fetch file');
      }

      const content = await response.text();
      
      // Validate based on file type
      const requiredColumns = fileType === 'pricingFile' 
        ? ['code', 'description', 'price']
        : ['jnpi', 'name', 'address', 'street', 'city', 'state', 'zip_code', 'latitude', 'longitude', 'phone', 'website'];
      
      validateCSVContent(content, requiredColumns, fileType);
      
      // Update state for specific company
      setDriveCompanies(companies => 
        companies.map(company => 
          company.id === companyId
            ? {
                ...company,
                [fileType]: { link, content }
              }
            : company
        )
      );

    } catch (error) {
      console.error('Error processing drive file:', error);
      setDriveError(`Error processing file: ${error.message}`);
    } finally {
      setIsProcessingDrive(false);
    }
  };

  // Update process function to handle multiple companies
  const processGoogleDriveFiles = () => {
    try {
      // Filter out companies without both files
      const validCompanies = driveCompanies.filter(
        c => c.pricingFile.content && c.locationFile.content
      );
      
      if (validCompanies.length === 0) {
        setDriveError('Please upload at least one complete set of files');
        return;
      }

      // Process each company's files and combine results
      let combinedPricingContent = '';
      let combinedLocationContent = '';

      validCompanies.forEach((company, index) => {
        const processedPricing = generateCombinedPricingContent(
          company.pricingFile.content,
          company.locationFile.content
        );

        // Get location rows and filter empty ones
        const locationRows = company.locationFile.content.split('\n')
          .filter(row => row.trim() !== '' && row.replace(/,/g, '').trim() !== '');

        if (index === 0) {
          combinedPricingContent = processedPricing;
          combinedLocationContent = locationRows.join('\n');
        } else {
          // Remove header before joining for subsequent files
          combinedPricingContent += '\n' + processedPricing.split('\n').slice(1).join('\n');
          combinedLocationContent += '\n' + locationRows.slice(1).join('\n');
        }
      });

      // Download combined files
      downloadFile(combinedPricingContent, 'combined_pricing.csv');
      downloadFile(combinedLocationContent, 'combined_locations.csv');

    } catch (error) {
      console.error('Error processing files:', error);
      setDriveError(`Error processing files: ${error.message}`);
    }
  };

  // Update the extractFileIdFromLink function
  const extractFileIdFromLink = (link) => {
    try {
      console.log('Processing link:', link); // Debug log

      // Handle different Google Drive link formats
      const patterns = [
        /\/spreadsheets\/d\/([a-zA-Z0-9_-]+)/,  // Spreadsheet format
        /\/file\/d\/([a-zA-Z0-9_-]+)/,          // General file format
        /id=([a-zA-Z0-9_-]+)/,                  // ID parameter format
        /\/d\/([a-zA-Z0-9_-]+)/                 // Short format
      ];

      for (const pattern of patterns) {
        const match = link.match(pattern);
        if (match && match[1]) {
          const fileId = match[1].split('/')[0];  // Remove any trailing path
          console.log('Successfully extracted file ID:', fileId);
          return fileId;
        }
      }
      
      throw new Error('Could not extract valid file ID from link');
    } catch (error) {
      console.error('Error extracting file ID:', error);
      return null;
    }
  };

  // Add a logout function
  const logout = () => {
    localStorage.removeItem('driveToken');
    setAccessToken(null);
  };

  // Add this function back near your other helper functions
  const validateCSVStructure = (file, requiredColumns, onSuccess, onError) => {
    const reader = new FileReader();
    
    reader.onload = (e) => {
      try {
        const text = e.target.result;
        const rows = text.split('\n')
          .filter(row => row.trim() !== '');
        
        if (rows.length < 2) {
          onError('File must contain at least a header row and one data row');
          return;
        }

        const headers = parseCSVLine(rows[0].toLowerCase());
        
        // Check if all required columns are present
        const missingColumns = requiredColumns.filter(col => 
          !headers.includes(col.toLowerCase())
        );

        if (missingColumns.length > 0) {
          onError(`Missing required columns: ${missingColumns.join(', ')}`);
          return;
        }

        onSuccess(text);
      } catch (err) {
        onError('Error processing CSV file: ' + err.message);
      }
    };

    reader.onerror = () => {
      onError('Error reading file');
    };

    reader.readAsText(file);
  };

  // Add function to handle new company rows
  const addDriveCompanyRow = () => {
    const newId = Math.max(...driveCompanies.map(c => c.id)) + 1;
    setDriveCompanies([...driveCompanies, {
      id: newId,
      pricingFile: { link: '', content: null },
      locationFile: { link: '', content: null }
    }]);
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <header>
        <h1>Jupiter Plans - Price List Generator</h1>
      </header>

      <main>
        <p className="intro-text">
          Welcome to the Pricing List Generator. This tool has been created to help you translate any cash pay provider's menu that you find so that it can be cleanly uploaded on Jupiter's servers. Using the first tool below, you can search Jupiter's proprietary pricing index for any category of services that you'd like. You can use this in Step 1 below to adapt the menu for how Jupiter categorizes services and then, in Step 2, combine that with a list of locations to generate a file that's ready to be uploaded to Jupiter's server.
        </p>

        <section>
          <h2>Step 1: Labeling Price Data</h2>
          <div className="section-content">
            <div className="dropdown-section">
              <p>Please select from the drop down below the pricing list that you want to see</p>
              <select 
                value={selectedPriceList} 
                onChange={(e) => setSelectedPriceList(e.target.value)}
                className="dropdown"
              >
                <option value="">Select a category...</option>
                <option value="urgent">Urgent Care</option>
                <option value="concierge">Concierge Primary Care</option>
                <option value="imaging">Imaging</option>
              </select>
            </div>

            <div className="format-instructions">
              <p>
                The pricing file that you are creating after this step needs to be a CSV file that has three columns - Code, Description, and Price. The "Description" column should be exactly how it is described on the website. If you do not follow this format, you will have an error in the following step.
              </p>
            </div>
          </div>

          {selectedPriceList && (
            <div className="table-container">
              <table>
                <thead>
                  <tr>
                    <th>Code</th>
                    <th>Description</th>
                  </tr>
                </thead>
                <tbody>
                  {tableData.map((row, index) => (
                    <tr key={index}>
                      <td>{row.code}</td>
                      <td>{row.description}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>

        <section className="file-section">
          <h2>Step 2: Process Company Files</h2>
          
          {combinedError && (
            <div className="file-error">{combinedError}</div>
          )}
          
          <div className="file-grid">
            <div className="file-grid-header">
              <div>Pricing Files</div>
              <div>Location Files</div>
            </div>
            
            {companies.map((company) => (
              <div key={company.id} className="file-company-row">
                <div>
                  <input
                    type="file"
                    accept=".csv"
                    className="file-input"
                    onChange={(e) => handleFileUpload(company.id, 'pricing', e)}
                  />
                  {company.pricingFile && (
                    <div className="file-success">✓ File loaded: {company.pricingFile.name}</div>
                  )}
                </div>
                
                <div>
                  <input
                    type="file"
                    accept=".csv"
                    className="file-input"
                    onChange={(e) => handleFileUpload(company.id, 'location', e)}
                  />
                  {company.locationFile && (
                    <div className="file-success">✓ File loaded: {company.locationFile.name}</div>
                  )}
                </div>
              </div>
            ))}
          </div>

          <div className="file-buttons">
            <button 
              onClick={addCompanyRow}
              className="file-add-button"
            >
              Add Company
            </button>

            <button
              onClick={processCombinedFiles}
              className="file-process-button"
              disabled={!companies.some(c => c.pricingFile && c.locationFile)}
            >
              Process Files
            </button>
          </div>
        </section>

        <section className="file-section">
          <h2>Step 3: Process Google Drive Files</h2>
          
          {driveError && (
            <div className="file-error">{driveError}</div>
          )}
          
          <div className="file-grid">
            <div className="file-grid-header">
              <div>Pricing File Links</div>
              <div>Location File Links</div>
            </div>
            
            {driveCompanies.map((company) => (
              <div key={company.id} className="file-company-row">
                <div>
                  <input
                    type="text"
                    className="file-input"
                    placeholder="Paste pricing file link"
                    onChange={(e) => handleDriveLink(company.id, 'pricingFile', e.target.value)}
                  />
                  {company.pricingFile.content && (
                    <div className="file-success">✓ File loaded successfully</div>
                  )}
                </div>
                
                <div>
                  <input
                    type="text"
                    className="file-input"
                    placeholder="Paste location file link"
                    onChange={(e) => handleDriveLink(company.id, 'locationFile', e.target.value)}
                  />
                  {company.locationFile.content && (
                    <div className="file-success">✓ File loaded successfully</div>
                  )}
                </div>
              </div>
            ))}
          </div>

          <div className="file-buttons">
            <button 
              onClick={addDriveCompanyRow}
              className="file-add-button"
            >
              Add Another Company
            </button>

            <button
              onClick={processGoogleDriveFiles}
              className="file-process-button"
              disabled={!driveCompanies.some(c => c.pricingFile.content && c.locationFile.content)}
            >
              Process Drive Files
            </button>
          </div>
        </section>
      </main>

      <button
        onClick={() => {
          localStorage.removeItem('isAuthenticated');
          setIsAuthenticated(false);
        }}
        className="text-sm text-gray-500 hover:text-gray-700"
      >
        Logout
      </button>
    </div>
  )
};

export default App
